export type AdminSurfaceAccess = "public" | "local-only" | "disabled";

export type AdminAccessPolicy =
  | { mode: "disabled" }
  | { mode: "local-dev"; allowPublicDevHost: boolean }
  | { mode: "platform-admin"; authorize?: (context: AdminAccessContext) => boolean | Promise<boolean> };

export type AdminAccessContext = {
  req: {
    header(name: string): string | null | undefined;
  };
  json(body: unknown, status?: number): unknown;
};

export type AdminAccessMiddleware = (
  context: AdminAccessContext,
  next: () => Promise<unknown>,
) => Promise<unknown>;

type AdminRouteRegistrar = {
  use?(path: string, handler: AdminAccessMiddleware): void;
};

export function resolveAdminSurfaceAccess(params: {
  isBaas: boolean;
  isPlatform: boolean;
}): AdminSurfaceAccess {
  return params.isBaas || params.isPlatform ? "local-only" : "public";
}

export function createAdminAccessPolicy(access: AdminSurfaceAccess): AdminAccessPolicy {
  if (access === "disabled") {
    return { mode: "disabled" };
  }
  return { mode: "local-dev", allowPublicDevHost: access === "public" };
}

export function shouldAllowAdminSurfaceHost(hostHeader: string | null | undefined): boolean {
  const raw = (hostHeader || "").trim().toLowerCase();
  if (raw === "::1") return true;

  const bracketedIpv6 = raw.match(/^\[([^\]]+)\](?::(\d{1,5}))?$/);
  if (bracketedIpv6) {
    const [, host, port] = bracketedIpv6;
    if (port && Number(port) > 65535) return false;
    return host === "::1";
  }

  const hostPort = raw.match(/^([^:]+)(?::(\d{1,5}))?$/);
  if (!hostPort) return false;
  const [, host, port] = hostPort;
  if (port && Number(port) > 65535) return false;
  return host === "localhost" || host === "127.0.0.1";
}

export function createAdminAccessMiddleware(policy: AdminAccessPolicy): AdminAccessMiddleware {
  return async (c, next) => {
    if (policy.mode === "disabled") {
      return c.json({ error: "Not found" }, 404);
    }

    if (policy.mode === "local-dev") {
      if (policy.allowPublicDevHost || shouldAllowAdminSurfaceHost(c.req.header("host"))) {
        return next();
      }
      return c.json({ error: "Not found" }, 404);
    }

    const allowed = await policy.authorize?.(c);
    if (!allowed) {
      return c.json({ error: "Forbidden" }, 403);
    }
    return next();
  };
}

export function applyAdminAccessMiddleware(registrar: AdminRouteRegistrar, policy?: AdminAccessPolicy): void {
  if (!policy) {
    return;
  }
  if (!registrar.use) {
    throw new Error("Admin access policy requires route registrar use()");
  }
  registrar.use("*", createAdminAccessMiddleware(policy));
}
